/**
 * Presentation rules from spec §13: money right-aligned with an explicit currency and tabular
 * figures, references in monospace, and every timestamp in CAT with a UTC tooltip.
 */

const CAT = 'Africa/Harare';

/** Money is formatted from its string form — it never becomes a JavaScript number on the way. */
export function money(amount: string | number | null | undefined, currency?: string | null): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const text = typeof amount === 'number' ? amount.toString() : amount;
  const [whole, fraction = ''] = text.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const cents = (fraction + '00').slice(0, 2);
  return `${currency ? currency + ' ' : ''}${grouped}.${cents}`;
}

export function catTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: CAT,
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** The tooltip carries UTC, so nobody has to do the arithmetic in their head. */
export function utcTooltip(iso: string | null | undefined): string {
  if (!iso) return '';
  return `${new Date(iso).toISOString().replace('T', ' ').slice(0, 19)} UTC`;
}

export function relativeAge(iso: string | null | undefined): string {
  if (!iso) return '—';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d`;
}

export function duration(seconds: number): string {
  if (seconds <= 0) return 'expired';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function percent(rate: string | number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${(Number(rate) * 100).toFixed(1)}%`;
}
